import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import { toast } from "react-hot-toast";
import { FiArrowLeft, FiCheck, FiX, FiRefreshCcw } from "react-icons/fi";

const SkeletonBlock = ({ className }) => <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>;

const AppDetailsSkeleton = () => {
  return (
    <div className="p-4 md:p-8 w-full">
      {/* Header Skeleton */}
      <div className="flex items-center gap-4 mb-6">
        <SkeletonBlock className="w-10 h-10 rounded-full" />
        <SkeletonBlock className="h-8 w-64" />
        <SkeletonBlock className="h-8 w-24 ml-auto rounded-full" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <SkeletonBlock className="h-6 w-48 mb-4" />
            <div className="border-b mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-6">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <SkeletonBlock className="h-3 w-24 mb-2" />
                  <SkeletonBlock className="h-5 w-40" />
                </div>
              ))}
            </div>
          </div>

          {/* Documents Card (Moved to Left Column) */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <SkeletonBlock className="h-6 w-32 mb-4" />
            <div className="border-b mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <SkeletonBlock className="h-3 w-28 mb-2" />
                  <SkeletonBlock className="h-40 w-full rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Actions Card */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <SkeletonBlock className="h-6 w-32 mb-4" />
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <SkeletonBlock key={i} className="h-10 w-full rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminVolunteerAppDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionType, setActionType] = useState(""); // "rejected"

  const getApplication = async () => {
    try {
      const resp = await axiosInstance.get(`/v2/admin/volunteer/applications/${id}`);
      setApplication(resp.data.application);
      setError(false);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getApplication();
  }, [id]);

  const handleUpdateStatus = async (status, reason = "") => {
    try {
      setStatusUpdating(true);
      await axiosInstance.post(`/v2/admin/volunteer/applications/${id}/status`, {
        status,
        rejectionReason: reason
      });
      toast.success(`Application marked as ${status.replace('_', ' ')}`);
      setShowRejectBox(false);
      setRejectionReason("");
      getApplication();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return <AppDetailsSkeleton />;
  }

  if (error || !application) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 text-lg">Failed to load application details.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 underline">Go Back</button>
      </div>
    );
  }

  const { basicInfo, documents, status } = application;
  const isPending = status !== "approved" && status !== "rejected";

  return (
    <div className="p-4 md:p-8 w-full">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
          <FiArrowLeft className="text-xl text-gray-700" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Application Details</h1>
        <span className={`ml-auto px-3 py-1 rounded-full text-sm font-semibold capitalize
          ${status === 'approved' ? 'bg-green-100 text-green-800' : 
            status === 'rejected' ? 'bg-red-100 text-red-800' : 
            'bg-gray-100 text-gray-800'}`}>
          {status.replace('_', ' ')}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase">Full Name</p>
                <p className="text-gray-900 font-medium">{basicInfo?.fullName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase">Phone</p>
                <p className="text-gray-900 font-medium">{basicInfo?.phone}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase">Email</p>
                <p className="text-gray-900 font-medium">{basicInfo?.email || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase">Address</p>
                <p className="text-gray-900 font-medium">{basicInfo?.address || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">Documents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {documents?.profilePhotoUrl && (
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Profile Photo</p>
                  <a href={documents.profilePhotoUrl} target="_blank" rel="noreferrer">
                    <img src={documents.profilePhotoUrl} alt="Profile" className="w-full h-40 object-cover rounded border hover:opacity-90 transition-opacity" />
                  </a>
                </div>
              )}
              {documents?.aadharUrl && (
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Aadhar Card</p>
                  <a href={documents.aadharUrl} target="_blank" rel="noreferrer">
                    <img src={documents.aadharUrl} alt="Aadhar" className="w-full h-40 object-cover rounded border hover:opacity-90 transition-opacity" />
                  </a>
                </div>
              )}
              {documents?.panUrl && (
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase mb-2">PAN Card</p>
                  <a href={documents.panUrl} target="_blank" rel="noreferrer">
                    <img src={documents.panUrl} alt="PAN" className="w-full h-40 object-cover rounded border hover:opacity-90 transition-opacity" />
                  </a>
                </div>
              )}
              {documents?.signatureUrl && (
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Signature</p>
                  <a href={documents.signatureUrl} target="_blank" rel="noreferrer">
                    <img src={documents.signatureUrl} alt="Signature" className="w-full h-40 object-contain bg-gray-50 rounded border hover:opacity-90 transition-opacity" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Actions */}
        <div className="space-y-6 lg:sticky lg:top-6">
          {/* Actions Panel */}
          {isPending && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Take Action</h2>
              
              {!showRejectBox ? (
                <div className="space-y-3">
                  <button 
                    onClick={() => handleUpdateStatus("approved")}
                    disabled={statusUpdating}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    <FiCheck /> Approve Application
                  </button>
                  <button 
                    onClick={() => { setShowRejectBox(true); setActionType("rejected"); }}
                    disabled={statusUpdating}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    <FiX /> Reject Application
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Reason for Rejection
                  </label>
                  <textarea
                    rows="3"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-primaryColor outline-none"
                    placeholder="Enter reason here..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setShowRejectBox(false)}
                      className="flex-1 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => handleUpdateStatus(actionType, rejectionReason)}
                      disabled={!rejectionReason.trim() || statusUpdating}
                      className="flex-1 py-2 bg-primaryColor text-white rounded hover:bg-teal-800 transition-colors disabled:opacity-50"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rejection Info */}
          {status === "rejected" && application.rejectionReason && (
            <div className={`p-4 rounded-lg border bg-red-50 border-red-200 text-red-800`}>
              <h3 className="font-bold mb-1">Feedback Provided:</h3>
              <p className="text-sm">{application.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVolunteerAppDetails;
